import { PitcherClientConnectionState } from "environment-interface/pitcher";
import { useReducerDevtools } from "features/utils/useReducerDevtools";
import { useEffect, useReducer } from "react";

import { createHookContext } from "utils/createHookContext";

import { useLegacyPitcher } from "../utils/usePitcher";

import { featureReducer } from "./reducer";
import type { State } from "./types";

export interface ProviderProps {
  children: React.ReactNode;
  initialState?: State;
  origin: string;
  initialPath?: string;
  devtoolId?: string;
  onPathChange?: (path: string) => void;
}

export const usePreview = createHookContext(
  ({
    origin,
    initialPath = "",
    initialState,
    devtoolId,
    onPathChange,
  }: ProviderProps) => {
    const pitcher = useLegacyPitcher();

    let initialUrl;
    try {
      // `new URL` can throw, and if you put a path with two slashes it crashes the editor
      // we now introduce a fallback in case this fails.
      initialUrl = initialPath ? new URL(initialPath, origin).href : origin;
    } catch (e) {
      initialUrl = origin;
    }
    const feature = useReducer(
      featureReducer,
      initialState ?? {
        state:
          pitcher.connectionState === PitcherClientConnectionState.Connected
            ? "READY"
            : "CONNECTING",
        origin,
        iframeSrc: initialUrl,
        url: initialUrl,
        urlInputValue: initialUrl,
        canGoBack: false,
        canGoForward: false,
      },
    );

    useReducerDevtools(devtoolId ? `preview-${devtoolId}` : "preview", feature);

    const [state, dispatch] = feature;

    useEffect(() => {
      if (origin && state.origin !== origin) {
        const newUrl = new URL(initialPath, origin).href;

        dispatch({
          type: "UPDATE_ORIGIN",
          newOrigin: origin,
          newUrl,
        });
      }
    }, [origin]);

    useEffect(() => {
      const path = state.url.replace(origin, "");
      onPathChange?.(path);
    }, [state.url]);

    return feature;
  },
);

export const PreviewConsumer = usePreview.Consumer;

export const PreviewProvider = usePreview.Provider;
